import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ColorPicker, Field, TimezoneSelect, VisibilityPicker } from '@/components/app/common';
import { api, del, patch, post } from '@/lib/api';
import { keys, useMe } from '@/lib/queries';
import { attempt, notify } from '@/lib/notify';
import { confirm } from '@/lib/confirm';
import { plural, ROLE_LABEL } from '@/lib/format';
import type { Role, Visibility } from '@/lib/types';
import type { TabProps } from './FeedPage';

export function SettingsTab({ id, detail, canEdit }: TabProps) {
  const { feed, role } = detail;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  const initial = useMemo(
    () => ({ name: feed.name, publisher: feed.publisher, timezone: feed.timezone, description: feed.description, source_url: feed.source_url ?? '', color: feed.color, visibility: feed.visibility, subscriber_layers: !!feed.subscriber_layers }),
    [feed],
  );
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial), [initial]);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!f.name.trim()) return notify('Name can’t be empty', { kind: 'error' });
    const body: Record<string, unknown> = { ...f };
    if (feed.kind !== 'external') delete body.source_url;
    if (role !== 'owner') delete body.visibility;
    setSaving(true);
    const ok = await attempt(() => patch(`/feeds/${id}`, body), 'Settings saved');
    setSaving(false);
    if (ok) await Promise.all([qc.invalidateQueries({ queryKey: keys.feed(id) }), qc.invalidateQueries({ queryKey: keys.feeds })]);
  }

  const members = useQuery({ queryKey: [...keys.feed(id), 'members'], queryFn: async () => (await api<{ members: { id: string; name: string; email: string; role: Role }[] }>(`/feeds/${id}/members`)).members });
  const [invite, setInvite] = useState({ email: '', role: 'editor' });
  const [adding, setAdding] = useState(false);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
      <form onSubmit={save}>
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-[minmax(0,1fr)] gap-5">
            <Field label="Name" htmlFor="s-name">
              <Input id="s-name" name="name" maxLength={80} disabled={!canEdit} value={f.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-2">
              <Field label="Publisher" htmlFor="s-pub" hint="Shown on the share page.">
                <Input id="s-pub" name="publisher" maxLength={80} disabled={!canEdit} value={f.publisher} onChange={(e) => set('publisher', e.target.value)} />
              </Field>
              <Field label="Time zone" htmlFor="s-tz" hint="All-day events aren’t affected.">
                <TimezoneSelect id="s-tz" value={f.timezone} disabled={!canEdit} onChange={(v) => set('timezone', v)} />
              </Field>
            </div>
            <Field label="Description" htmlFor="s-desc">
              <Textarea id="s-desc" name="description" rows={2} maxLength={500} disabled={!canEdit} placeholder="Optional — shown on the share page" value={f.description} onChange={(e) => set('description', e.target.value)} />
            </Field>
            {feed.kind === 'external' ? (
              <Field label="Source URL" htmlFor="s-src">
                <Input id="s-src" name="source_url" disabled={!canEdit} value={f.source_url} onChange={(e) => set('source_url', e.target.value)} />
              </Field>
            ) : null}
            <Field label="Color">
              <ColorPicker value={f.color} onChange={(c) => canEdit && set('color', c)} />
            </Field>
            {role === 'owner' ? (
              <Field label="Who can subscribe?">
                <VisibilityPicker value={f.visibility} onChange={(v: Visibility) => set('visibility', v)} />
              </Field>
            ) : null}
            <Field label="Layers for subscribers" hint="They pick on the share page or in an embedded calendar and get a personal link. Your own events and changes are always included.">
              <div className="flex items-center gap-2">
                <Switch id="s-layers" disabled={!canEdit} checked={f.subscriber_layers} onCheckedChange={(v) => set('subscriber_layers', v)} />
                <Label htmlFor="s-layers" className="font-normal">
                  Subscribers can leave out layers they don’t need
                </Label>
              </div>
            </Field>
          </CardContent>
          {canEdit ? (
            <CardFooter className="bg-card sticky bottom-0 z-10 justify-between gap-2 border-t py-4">
              <span className="text-muted-foreground text-sm" aria-live="polite">
                {dirty ? 'You have unsaved changes' : ''}
              </span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={!dirty} onClick={() => setF(initial)}>
                  Discard
                </Button>
                <Button type="submit" disabled={!dirty || saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </CardFooter>
          ) : null}
        </Card>
      </form>

      <Card>
        <CardHeader>
          <CardTitle>People</CardTitle>
          <CardDescription>Editors can manage events, layers and changes. Viewers can see this calendar even when it’s private. Subscribers don’t need to be added here.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-[minmax(0,1fr)] gap-4">
          <div className="divide-y">
            {(members.data ?? []).map((m) => (
              <div key={m.id} className="flex items-center gap-3 py-2.5">
                <Avatar className="size-8">
                  <AvatarFallback className="text-xs font-semibold">{(m.name || m.email)[0].toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {m.name}
                    {m.id === me.data?.id ? ' (you)' : ''}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">{m.email}</p>
                </div>
                <Badge variant={m.role === 'owner' ? 'default' : 'secondary'}>{ROLE_LABEL[m.role]}</Badge>
                {role === 'owner' && m.role !== 'owner' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={async () => {
                      if (!(await confirm({ title: `Remove ${m.name}?`, body: 'They’ll lose access to this calendar.', confirm: 'Remove', danger: true }))) return;
                      if (await attempt(() => del(`/feeds/${id}/members/${m.id}`), 'Removed')) members.refetch();
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
          {role === 'owner' ? (
            <form
              className="flex flex-wrap gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                setAdding(true);
                const ok = await attempt(() => post(`/feeds/${id}/members`, invite), 'Person added');
                setAdding(false);
                if (ok) {
                  setInvite({ email: '', role: invite.role });
                  members.refetch();
                }
              }}
            >
              <Input name="email" type="email" required placeholder="Their account email" aria-label="Email" className="min-w-52 flex-1" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
              <select aria-label="Role" name="role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="border-input dark:bg-input/30 h-9 rounded-md border bg-transparent px-3 text-sm shadow-xs">
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <Button type="submit" variant="outline" disabled={adding}>
                {adding ? 'Adding…' : 'Add person'}
              </Button>
            </form>
          ) : null}
        </CardContent>
      </Card>

      {role === 'owner' ? (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle>Delete calendar</CardTitle>
            <CardDescription>
              The subscribe link stops working immediately{detail.downstream_count ? `, and ${plural(detail.downstream_count, 'calendar')} that include this one will lose its events` : ''}. This can’t be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!(await confirm({ title: 'Delete this calendar?', body: 'All events, changes and history will be permanently deleted.', confirm: 'Delete forever', danger: true, typeToConfirm: feed.name }))) return;
                if (!(await attempt(() => del(`/feeds/${id}`), 'Calendar deleted'))) return;
                qc.removeQueries({ queryKey: keys.feed(id) });
                await qc.invalidateQueries({ queryKey: keys.feeds });
                navigate('/');
              }}
            >
              Delete this calendar
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
